import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Send, Loader2, RefreshCw, Search, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'booking_confirmed', label: 'Booking Confirmed', channel: 'ss_class' },
  { value: 'session_cancelled', label: 'Session Cancelled', channel: 'ss_class' },
  { value: 'live_class_starting', label: 'Class Starting (1hr)', channel: 'ss_class' },
  { value: 'session_rescheduled', label: 'Session Rescheduled', channel: 'ss_class' },
  { value: 'session_recording_ready', label: 'Recording Ready', channel: 'ss_class' },
  { value: 'instructor_changed', label: 'Instructor Changed', channel: 'ss_class' },
  { value: 'new_session_assigned', label: 'New Session Assigned', channel: 'ss_class' },
  { value: 'consult_starting', label: 'Consult Starting', channel: 'ss_doctor' },
  { value: 'doctor_chat', label: 'Doctor Chat', channel: 'ss_doctor' },
  { value: 'payment_success', label: 'Payment Success', channel: 'ss_general' },
  { value: 'subscription_expiring', label: 'Subscription Expiring', channel: 'ss_general' },
  { value: 'daily_tip', label: 'Daily Tip', channel: 'ss_daily' },
  { value: 'edd_countdown', label: 'EDD Countdown', channel: 'ss_daily' },
  { value: 'baby_milestone_due', label: 'Baby Milestone', channel: 'ss_postnatal' },
  { value: 'weekly_weight_log', label: 'Weekly Weight Log', channel: 'ss_postnatal' },
  { value: 'promotional', label: 'Promotional', channel: 'ss_promo' },
  { value: 'test_push', label: 'Test Push (generic)', channel: 'ss_general' },
];

const DEFAULT_BODIES = {
  booking_confirmed: 'Prenatal Yoga on Jul 15. Tap to view details.',
  session_cancelled: 'Your 6:30 PM class on Jul 15 has been cancelled.',
  live_class_starting: 'Your class starts in 1 hour — get ready!',
  session_rescheduled: 'Your class moved to Jul 16 at 6:30 PM.',
  session_recording_ready: 'Catch up on Prenatal Yoga — recording available now.',
  consult_starting: 'Your consult with Ankita starts soon.',
  doctor_chat: 'Ankita: Your diet plan is ready.',
  payment_success: 'Your payment of INR 4999 for Elite Plan is confirmed.',
  daily_tip: 'Week 28: Practice deep belly breathing for 5 min today.',
  test_push: 'If you see this, push notifications are working!',
};

const DEFAULT_TITLES = {
  booking_confirmed: 'Class booked',
  session_cancelled: 'Class cancelled',
  live_class_starting: 'Class in 1 hour',
  session_rescheduled: 'Class rescheduled',
  session_recording_ready: 'Recording ready',
  consult_starting: 'Consult in 15 min',
  doctor_chat: 'New message',
  payment_success: 'Payment received',
  daily_tip: 'Tip of the day',
  test_push: 'Soul Sanskar test',
};

export default function TestPushPage() {
  const [method, setMethod] = useState('user');
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fcmToken, setFcmToken] = useState('');
  const [eventType, setEventType] = useState('test_push');
  const [title, setTitle] = useState('Soul Sanskar test');
  const [body, setBody] = useState('If you see this, push notifications are working!');
  const [sessionId, setSessionId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [consultationId, setConsultationId] = useState('');
  const [extraData, setExtraData] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [userSessions, setUserSessions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [tokenDebug, setTokenDebug] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const res = await api.get('/api/admin/ops/fcm/test-push-log?limit=20');
      setLog(res.data.log || []);
    } catch { /* ignore */ }
    setLoadingLog(false);
  }, []);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  // User search
  useEffect(() => {
    if (method !== 'user' || userSearch.length < 2) { setUserResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/api/users?search=${encodeURIComponent(userSearch)}&limit=8`);
        setUserResults(res.data.users || []);
      } catch { setUserResults([]); }
      setSearching(false);
    }, 400);
    return () => clearTimeout(t);
  }, [userSearch, method]);

  // Fetch user sessions when user selected
  useEffect(() => {
    if (!selectedUser) { setUserSessions([]); setTokenDebug(null); return; }
    (async () => {
      try {
        const res = await api.get(`/api/admin/ops/fcm/user-sessions/${selectedUser.id}`);
        setUserSessions(res.data.sessions || []);
      } catch { setUserSessions([]); }
    })();
    // Also fetch token debug info
    (async () => {
      setLoadingDebug(true);
      try {
        const res = await api.get(`/api/admin/ops/fcm/debug-user/${selectedUser.id}`);
        setTokenDebug(res.data);
      } catch { setTokenDebug(null); }
      setLoadingDebug(false);
    })();
  }, [selectedUser]);

  // Auto-fill title/body when event type changes
  useEffect(() => {
    setTitle(DEFAULT_TITLES[eventType] || 'Soul Sanskar test');
    setBody(DEFAULT_BODIES[eventType] || 'Test notification from admin console.');
  }, [eventType]);

  const handleSend = async () => {
    if (method === 'user' && !selectedUser) { toast.error('Select a user first'); return; }
    if (method === 'token' && !fcmToken.trim()) { toast.error('Paste an FCM token'); return; }
    if (!title.trim() || !body.trim()) { toast.error('Title and body are required'); return; }

    let parsedExtra = {};
    if (extraData.trim()) {
      try { parsedExtra = JSON.parse(extraData); } catch { toast.error('Extra data must be valid JSON'); return; }
    }

    setSending(true);
    try {
      const payload = {
        method,
        user_id: method === 'user' ? selectedUser?.id : '',
        fcm_token: method === 'token' ? fcmToken : '',
        event_type: eventType,
        title,
        body,
        session_id: sessionId,
        course_id: courseId,
        consultation_id: consultationId,
        extra_data: parsedExtra,
      };
      const res = await api.post('/api/admin/ops/fcm/fire-test-push', payload);
      const d = res.data;
      if (d.tokens_delivered > 0) {
        toast.success(`Push sent to ${d.tokens_delivered} device(s)`);
      } else {
        toast.error(`0/${d.tokens_tried} tokens delivered — check log below`);
      }
      fetchLog();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.data?.detailObject?.code === 'NO_FCM_TOKENS') {
        toast.error(detail || 'User has no FCM tokens registered');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Push send failed');
      }
      fetchLog();
    }
    setSending(false);
  };

  const evtMeta = EVENT_TYPES.find(e => e.value === eventType);

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6" data-testid="test-push-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Fire Test Push</h1>
        <p className="text-sm text-muted-foreground mt-1">Send a test FCM push notification to any user or device token. Bypasses user preferences.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Push Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Method toggle */}
          <div className="flex gap-2">
            <Button variant={method === 'user' ? 'default' : 'outline'} size="sm" onClick={() => setMethod('user')} data-testid="method-user">By User</Button>
            <Button variant={method === 'token' ? 'default' : 'outline'} size="sm" onClick={() => setMethod('token')} data-testid="method-token">By FCM Token</Button>
          </div>

          {/* User search */}
          {method === 'user' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Target User</label>
              {selectedUser ? (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedUser.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedUser.email} · {selectedUser.id}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserSearch(''); }} data-testid="clear-user">Change</Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="pl-9"
                    data-testid="user-search-input"
                  />
                  {searching && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
                  {userResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {userResults.map(u => (
                        <button key={u.id} className="w-full px-3 py-2 text-left hover:bg-muted/50 text-sm" onClick={() => { setSelectedUser(u); setUserResults([]); setUserSearch(''); }} data-testid={`user-result-${u.id}`}>
                          <span className="font-medium">{u.name}</span>
                          <span className="text-muted-foreground ml-2">{u.email}</span>
                          <Badge variant="outline" className="ml-2 text-[10px]">{u.role}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Token Debug Panel — shows when a user is selected */}
          {method === 'user' && selectedUser && (
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2" data-testid="token-debug-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Registered FCM Tokens</span>
                {loadingDebug && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
              {tokenDebug && tokenDebug.tokens_count === 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>No FCM tokens registered. User needs to open the app first.</span>
                </div>
              )}
              {tokenDebug && tokenDebug.tokens_count > 0 && tokenDebug.tokens.map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-background p-2 rounded border">
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{t.platform}</Badge>
                      <span className="text-muted-foreground">device: {t.device_id?.substring(0, 12)}...</span>
                    </div>
                    <p className="font-mono text-[10px] text-muted-foreground truncate" title={t.fcm_token_full}>
                      {t.fcm_token_full?.substring(0, 50)}...
                    </p>
                    <p className="text-[10px] text-muted-foreground">Updated: {t.updated_at ? new Date(t.updated_at).toLocaleString('en-IN') : '?'}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => {
                    navigator.clipboard.writeText(t.fcm_token_full);
                    toast.success('Full FCM token copied');
                  }}>Copy</Button>
                </div>
              ))}
            </div>
          )}

          {/* FCM token input */}
          {method === 'token' && (
            <div className="space-y-1">
              <label className="text-sm font-medium">FCM Token</label>
              <Textarea
                placeholder="Paste the raw FCM device token..."
                value={fcmToken}
                onChange={e => setFcmToken(e.target.value)}
                rows={2}
                className="font-mono text-xs"
                data-testid="fcm-token-input"
              />
            </div>
          )}

          {/* Event type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Event Type</label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(e => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                      <span className="text-muted-foreground ml-1 text-xs">({e.channel})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Channel</label>
              <div className="h-9 flex items-center px-3 rounded-md border bg-muted/30 text-sm">
                {evtMeta?.channel || 'ss_general'}
              </div>
            </div>
          </div>

          {/* Title + Body */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="push-title-input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Body</label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} rows={2} data-testid="push-body-input" />
          </div>

          {/* Optional IDs */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Session ID (optional)</label>
              {userSessions.length > 0 ? (
                <Select value={sessionId || "__none__"} onValueChange={v => setSessionId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Pick or type..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {userSessions.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.scheduled_date} {s.scheduled_time} ({s.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input placeholder="abc123..." value={sessionId} onChange={e => setSessionId(e.target.value)} className="text-xs" data-testid="session-id-input" />
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Course ID (optional)</label>
              <Input placeholder="abc123..." value={courseId} onChange={e => setCourseId(e.target.value)} className="text-xs" data-testid="course-id-input" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Consultation ID (optional)</label>
              <Input placeholder="abc123..." value={consultationId} onChange={e => setConsultationId(e.target.value)} className="text-xs" data-testid="consultation-id-input" />
            </div>
          </div>

          {/* Extra data */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Extra data (JSON, optional)</label>
            <Textarea
              placeholder='{"batch_name": "Prenatal Yoga", "date_str": "2026-07-15"}'
              value={extraData}
              onChange={e => setExtraData(e.target.value)}
              rows={2}
              className="font-mono text-xs"
              data-testid="extra-data-input"
            />
          </div>

          {/* Send button */}
          <Button onClick={handleSend} disabled={sending} className="w-full" data-testid="send-test-push-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send Test Push
          </Button>
        </CardContent>
      </Card>

      {/* Log */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Last 20 Sends</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchLog} disabled={loadingLog} data-testid="refresh-log-btn">
              <RefreshCw className={`h-4 w-4 ${loadingLog ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No test pushes sent yet.</p>
          ) : (
            <div className="space-y-2">
              {log.map((entry, i) => (
                <div key={entry.id || i} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/30 text-sm" data-testid={`log-entry-${i}`}>
                  <div className="mt-0.5">
                    {entry.status === 'delivered' ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : entry.status === 'no_tokens' ? (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{entry.event_type}</Badge>
                      <Badge variant={entry.status === 'delivered' ? 'default' : 'destructive'} className="text-[10px]">
                        {entry.status === 'delivered' ? `${entry.tokens_delivered}/${entry.tokens_tried} delivered` : entry.status === 'no_tokens' ? 'No tokens' : 'Failed'}
                      </Badge>
                      {entry.method === 'user' && entry.target_name && (
                        <span className="text-xs text-muted-foreground">{entry.target_name} ({entry.target_email})</span>
                      )}
                      {entry.method === 'token' && entry.fcm_token_prefix && (
                        <span className="text-xs text-muted-foreground font-mono">{entry.fcm_token_prefix}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      <strong>{entry.title}</strong>: {entry.body}
                    </p>
                  </div>
                  <div className="text-[10px] text-muted-foreground whitespace-nowrap flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {entry.sent_at ? new Date(entry.sent_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
